import React from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import _startCase from 'lodash/startCase'
import { retrieveConfig } from '../../config/config'
import PaymentModelLoader from '../payment-model-loader'
import GeneralError from '../general-error'
import { validate as isValidUUID } from 'uuid'
import ReceiptSuccess from './success'
import ReceiptFailure from './failure'
import { mapErrorMessage } from '../../utils/error-message-mapper'
import { fromMinorUnits } from '../../utils/minor-units'

export default function Receipt() {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const chargeId = queryParams.get('charge_id')
    const status = queryParams.get('status') // Get status from URL params

    const [receiptDetails, setReceiptDetails] = React.useState(null)
    const [config, setConfig] = React.useState(null)
    const [paymentStatus, setPaymentStatus] = React.useState(status || 'loading')

    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)

    React.useEffect(() => {
        ;(async function loadConfig() {
            const result = await retrieveConfig()
            setConfig(result)
        })()
    }, [])

    React.useEffect(() => {
        getReceiptData(chargeId, setLoading, setError)
            .then(data => {
                // Check payment status from the data

                const paymentCompleted = getPaymentStatus(data) === 'payment.completed'
                const hasError = data.error_message || data.psp_responses?.[0]?.psp_error
                const isRetryable = data.error_message || data.psp_responses?.[0]?.psp_error?.is_retryable

                if (paymentCompleted && !hasError) {
                    setPaymentStatus('success')
                    setReceiptDetails(buildReceiptDetails(data))
                } else {
                    setPaymentStatus('failed')
                    const rawErrorMsg =
                        data.error_message ||
                        data.psp_responses?.[0]?.psp_error?.error_message ||
                        'Payment was not completed successfully.'

                    // Map the raw error message to a user-friendly message
                    const mappedError = mapErrorMessage(rawErrorMsg)

                    setError({
                        message: mappedError.message,
                        isRetryable: isRetryable,
                        receiptData: data,
                    })
                }
            })
            .catch(e => {
                setPaymentStatus('failed')
                setError('Failed to retrieve receipt data.')
                console.error('Failed to retrieve receipt data', e)
            })
    }, [chargeId])

    const isSuccess = paymentStatus === 'success' && receiptDetails && !error
    const isFailure = paymentStatus === 'failed' || error

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                height: '100vh',
                backgroundColor: config?.backgroundColor,
            }}
        >
            <div style={{ width: 560, margin: '80px 0px' }}>
                <GeneralError visible={error && !isFailure} message={error} />
                {loading && <PaymentModelLoader />}
                {!loading && isSuccess && <ReceiptSuccess visible={true} chargeId={chargeId} receiptDetails={receiptDetails} />}
                {!loading && isFailure && <ReceiptFailure visible={true} chargeId={chargeId} message={error} />}
            </div>
        </div>
    )
}

async function getReceiptData(chargeId, setLoading) {
    if (!isValidUUID(chargeId)) throw new Error('Invalid charge ID')
    setLoading(true)
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    // Base URL is deployment-controlled env config; path is hardcoded; chargeId is UUID-validated above
    return axios
        .get(window.env.REACT_APP_DEMO_BACKEND_URL + '/payment/receipt/' + chargeId, {
            headers: {
                domain: window.location.hostname,
            },
        })
        .then(receiptResponse => receiptResponse.data)
        .finally(() => {
            setLoading(false)
        })
}

function getPaymentStatus(data) {
    const topLevelStatus = data?.status?.status

    // Check top-level status first
    if (topLevelStatus === 'payment.completed' || topLevelStatus === 'completed') {
        return 'payment.completed'
    }

    // Check if payment exists and has a completed/successful status
    if (!data?.payments?.[0]) {
        return 'payment.failed'
    }

    const payment = data.payments[0]
    const paymentStatus = payment?.status?.status?.toLowerCase()

    // Check for common success status values
    return paymentStatus === 'pending_authorisation' ? 'payment.completed' : 'payment.failed'
}
const getWalletName = walletId => {
    // if Doku wallet, get the name from the middle of the ID
    if (walletId.includes('_')) {
        const parts = walletId.split('_')
        return _startCase(parts[1])
    }

    return _startCase(walletId)
}

export const buildReceiptDetails = receiptData => {
    const paymentInstrument = receiptData.payments[0].payment_instrument
    const instrument = paymentInstrument.instrument
    const instrumentType = paymentInstrument.instrument_type
    const pspResponse = receiptData?.psp_responses ? receiptData?.psp_responses[0] : {}
    const pspName = pspResponse?.psp_name?.toLowerCase()

    // Determine payment type based on instrument type and PSP
    let paymentTypeValue = 'Card'
    if (
        instrumentType === 'wallet_india_instrument' ||
        instrumentType === 'upi_wallet_instrument' ||
        instrumentType === 'wallet_instrument'
    ) {
        paymentTypeValue = 'Wallet'
    } else if (instrumentType === 'netbanking_instrument') {
        paymentTypeValue = 'Netbanking'
    } else if (instrumentType === 'qr_instrument' && pspName === 'doku') {
        paymentTypeValue = 'QRIS'
    } else if (instrumentType === 'qr_code') {
        paymentTypeValue = 'VietQR'
    } else if (instrumentType === 'card_instrument' || instrumentType === 'card') {
        paymentTypeValue = 'Card'
    } else if (instrumentType === 'mobile_banking') {
        paymentTypeValue = 'Mobile Banking'
    }

    let payDetails = {}
    if (instrumentType === 'card_instrument' || instrumentType === 'card') {
        // Handle card number formatting
        const cardNum = instrument?.card_number || ''
        const cardLastFour = instrument?.last_four || ''
        let cardholderNumber = '.... .... .... ....'

        if (cardNum) {
            const cardStartingNumbers = cardNum.substring(0, 6)
            const cardEndingNumbers = cardNum.substring(cardNum.length - 4, cardNum.length)
            cardholderNumber = cardStartingNumbers + '....' + cardEndingNumbers
        } else if (cardLastFour) {
            cardholderNumber = '.... .... .... ' + cardLastFour
        }

        const expiryDate = instrument.expiration_date ?? '../..'

        // Get cardholder name from instrument or payer
        const cardholderName = instrument.card_holder_name || paymentInstrument.payer?.entity_details?.name?.full_name

        payDetails = {
            cardholderName: cardholderName,
            cardholderNumber: cardholderNumber,
            expiryDate: expiryDate,
        }
    } else if (instrumentType === 'wallet_india_instrument') {
        payDetails = {
            walletName: _startCase(instrument.wallet_india_name) ?? 'Unknown',
        }
    } else if (instrumentType === 'upi_wallet_instrument') {
        payDetails = {
            walletName: getWalletName(instrument.wallet_name) ?? 'Unknown',
        }
    } else if (instrumentType === 'wallet_instrument') {
        payDetails = {
            walletName: _startCase(instrument.wallet_brand) ?? 'Unknown',
        }
    } else if (instrumentType === 'netbanking_instrument') {
        payDetails = {
            bankName: instrument.bank_code || _startCase(instrument.acquirer),
        }
    }

    const rawAmount = receiptData.order?.order_amount?.value
    const orderCurrencyCode = receiptData.order?.order_amount?.currency?.currency_code
    const paymentAmount = rawAmount != null ? fromMinorUnits(rawAmount, orderCurrencyCode) : rawAmount

    const commonPaymentFields = {
        receiptNo: receiptData?.charge_id || '',
        // status: 'Approved',
        // response: 'Honour with identification (08)',
        paymentDate: receiptData.session?.start_timestamp || null,
        paymentInstrument: {
            ...instrument,
            instrument_type: instrumentType,
        },
        paymentAmount: paymentAmount,
        paymentType: paymentTypeValue,
    }

    return {
        ...commonPaymentFields,
        ...payDetails,
    }
}
